import type { SavedLocation, SavedLocationContact } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { useContactDrawerStore } from '@hero/hooks-shared';
import {
  CircleCheckIcon,
  ContactIcon,
  PhoneIcon,
  MailIcon,
  MapPinnedIcon,
} from '@hero/web-icons';
import { DrawerContentList } from '../../../app/Drawer/DrawerContentList';
import { ClientDisplay } from '../../Client/ClientDisplay';
import { Card } from '../../../app/Card/Card';

export interface SavedLocationSummaryTabProps {
  savedLocation: SavedLocation;
}

function ContactInlineCard({ contact }: { contact: SavedLocationContact }) {
  const openDrawer = useContactDrawerStore(s => s.open);

  return (
    <Card onClick={() => openDrawer(String(contact._id))}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700 min-w-0">
        <div className="flex items-center justify-center rounded-md bg-gray-50 p-1.5 flex-shrink-0">
          <ContactIcon className="size-3.5 text-gray-400" />
        </div>
        <span className="truncate">{contact.name}</span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
        {contact.phone && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <PhoneIcon className="size-3 text-gray-400" />
            <span>{contact.phone}</span>
          </div>
        )}
        {contact.email && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <MailIcon className="size-3 text-gray-400" />
            <span>{contact.email}</span>
          </div>
        )}
      </div>
    </Card>
  );
}

export function SavedLocationSummaryTab({ savedLocation }: SavedLocationSummaryTabProps) {
  const { t } = useTranslation(['m-location']);

  const hasClient = !!savedLocation.client;
  const locationType = hasClient ? 'for_client' : 'my_location';
  const contacts = savedLocation.contacts ?? [];
  const address = savedLocation.address?.fullAddress?.trim();
  const isApproximate = savedLocation.isAccurate === false;

  return (
    <div className="flex flex-col gap-5 px-5 py-4">
      {/* The address is the reason this drawer is open, so it gets a
          full-width block instead of a key/value row — the drawer header
          can only show it truncated, and Saudi addresses are long. */}
      <div className="flex flex-col gap-3 rounded-xl border border-gray-100 bg-gray-50/60 p-3.5">
        <div className="flex items-start gap-2.5">
          <span className="flex-none rounded-lg bg-white p-1.5 shadow-sm">
            <MapPinnedIcon className="size-4 text-primary" />
          </span>
          <p className="min-w-0 flex-1 break-words text-sm leading-relaxed text-gray-800">
            {address || t('drawer.fields.address')}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isApproximate ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
              {t('drawer.approximate_pin', { defaultValue: 'Approximate pin' })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              <CircleCheckIcon className="size-3" />
              {t('drawer.exact_pin', { defaultValue: 'Exact pin' })}
            </span>
          )}
          {savedLocation.locationUrl ? (
            <a
              href={savedLocation.locationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ms-auto inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline">
              <MapPinnedIcon className="size-3.5" />
              <span>{t('drawer.open_in_google_maps')}</span>
            </a>
          ) : null}
        </div>
      </div>

      {/* Name and address live in the drawer header already — repeating
          them here is what made this tab feel like a dump of rows. */}
      <DrawerContentList
        list={[
          {
            label: t('drawer.fields.type'),
            value: t(`location_type.${locationType}.title`),
          },
          {
            label: t('drawer.fields.client'),
            value: savedLocation.client?.name ? (
              <ClientDisplay clientName={savedLocation.client.name} />
            ) : (
              '-'
            ),
            hidden: !hasClient,
          },
        ]}
      />

      <div>
        <h4 className="mb-3 text-sm font-medium text-gray-500">
          {t('drawer.fields.contacts')}
        </h4>
        {contacts.length > 0 ? (
          <div className="flex flex-col gap-2">
            {contacts.map(contact => (
              <ContactInlineCard key={String(contact._id)} contact={contact} />
            ))}
          </div>
        ) : (
          // Contacts are optional now (a pin saved from chat often has
          // none), so an empty state beats hiding the section and leaving
          // the user wondering where contacts went.
          <p className="rounded-xl border border-dashed border-gray-200 px-3.5 py-3 text-xs text-gray-500">
            {t('drawer.no_contacts')}
          </p>
        )}
      </div>
    </div>
  );
}
